import type { BarkKind } from "../ai/barks.js";
import type { RaidEnv } from "../sim/env.js";
import type { LimbId } from "../combat/health.js";
import type { LoyaltyState } from "../economy/loyalty.js";
import type { QuestState } from "../economy/quests.js";
import type { HideoutState } from "../hideout/state.js";
import type { EquipmentRecord } from "../inventory/body.js";
import type { GridState } from "../inventory/grid.js";
import type { InventoryOp } from "../inventory/ops.js";
import type { Inventory } from "../inventory/state.js";
import type { ScavRole } from "../items/scavLoadouts.js";
import type { WildlifeSpecies } from "../sim/wildlife.js";
import type { TrackKind } from "../sim/tracks.js";
import type { TacticalKind, WeaponEquipSlot } from "../items/types.js";
import type { ProgressState, SkillMods } from "../meta/progress.js";
import type { ContainerKind } from "../maps/types.js";
import type { Vec3 } from "../math/vec3.js";

// ---------------------------------------------------------------------------
// Shared vocabulary
// ---------------------------------------------------------------------------

export type Stance = "stand" | "crouch";

/**
 * What a moving thing in the raid is. Every `"animal"` snapshot carries
 * `species`.
 */
export type EntityKind = "player" | "scav" | "animal";

export type AnimState = "idle" | "walk" | "sprint" | "dead";

/** Button bitmask carried by every input command. */
export const BTN = {
  FWD: 1,
  BACK: 2,
  LEFT: 4,
  RIGHT: 8,
  SPRINT: 16,
  CROUCH: 32,
  JUMP: 64,
  ADS: 128,
  RELOAD: 256,
  INTERACT: 512,
  /** Debug fly mode (F6): movement ignores collision and gravity while set. */
  NOCLIP: 1024,
  /**
   * Q and E: peek left / right. Two bits, since both keys can be down;
   * `leanFromButtons` collapses them. Not movement — `stepMovement` never
   * reads these.
   */
  LEAN_L: 2048,
  LEAN_R: 4096,
  /**
   * T: the mounted lamp or laser is on. Client-latched like `NOCLIP` (the key
   * toggles, the bit rides every command). The server only mirrors it into
   * `EntitySnap.tac`, and only when the active weapon carries a device.
   */
  TACTICAL: 8192,
} as const;

export type ButtonName = keyof typeof BTN;

/** Every bit currently defined — anything outside this is rejected by validate. */
export const BTN_ALL_MASK =
  BTN.FWD |
  BTN.BACK |
  BTN.LEFT |
  BTN.RIGHT |
  BTN.SPRINT |
  BTN.CROUCH |
  BTN.JUMP |
  BTN.ADS |
  BTN.RELOAD |
  BTN.INTERACT |
  BTN.NOCLIP |
  BTN.LEAN_L |
  BTN.LEAN_R |
  BTN.TACTICAL;

/** Max commands the client may batch into one `input` message. */
export const MAX_CMDS_PER_INPUT = 4;

/** Max characters in a player name. */
export const MAX_NAME_LENGTH = 24;

/**
 * One tick of player intent. `seq` is a monotonically increasing counter the
 * server echoes back as `lastAckSeq` so the client can reconcile prediction.
 */
export interface InputCmd {
  seq: number;
  /** Bitmask of `BTN.*`. */
  buttons: number;
  /** Radians, world yaw. */
  yaw: number;
  /** Radians, clamped to ±π/2. */
  pitch: number;
}

// ---------------------------------------------------------------------------
// Client -> server
// ---------------------------------------------------------------------------

export type ClientMsg =
  | { t: "hello"; protocol: number; name: string; token: string }
  | { t: "input"; cmds: InputCmd[] }
  /** Select the weapon carried in hotkey slot 1, 2, or 3. */
  | { t: "weaponSelect"; slot: WeaponEquipSlot }
  | { t: "ping"; cNow: number }
  /**
   * One trigger pull. Separate from `input` because it carries geometry:
   *   tick        client's estimate of the server tick; rate-limit and future check.
   *   renderTick  the tick remote entities were RENDERED at (one interpolation
   *               delay back) — what lag compensation rewinds to.
   *   origin      predicted eye position; the server clamps it.
   *   dir         unit direction after client-side recoil and spread (the
   *               client owns the roll so its tracer matches).
   */
  | { t: "fire"; tick: number; renderTick: number; origin: Vec3; dir: Vec3 }
  /**
   * Re-enter at a spawn point after dying. Direct-mode only — a meta server
   * refuses it (dying ends the raid).
   */
  | { t: "respawn" }
  /**
   * Queue for the next raid. Menu phase only; answered with `queueStatus`, then
   * `welcome` when the raid starts.
   */
  | { t: "queueJoin" }
  /** Take me out again. Idempotent: leaving a queue you are not in is fine. */
  | { t: "queueLeave" }
  /**
   * Buy ONE offer: `buyQty(defId)` units (a full box of ammo, one of anything
   * else) at `buyPrice`. No quantity field — one message per click. `opId`
   * shares the `invOp` counter and is answered with `invResult` (a purchase is
   * an optimistic inventory edit awaiting a verdict), then a `profile`.
   */
  | { t: "buy"; opId: number; traderId: string; defId: string }
  /**
   * Sell ONE instance out of the STASH — worn/kit gear must be moved there
   * first, which is the confirmation step. The whole instance goes (stack,
   * mods, contents, priced by `sellPriceDeep`); a box crosses the counter only
   * if every item in it is buyable, else "not-empty"/"not-interested".
   * Answered via `invResult`, then a `profile`.
   */
  | { t: "sell"; opId: number; traderId: string; iid: string }
  /**
   * Repair ONE armour instance to its ceiling. Menu only; unlike `sell` it
   * reaches anywhere the profile owns the plate (a repair destroys nothing).
   * Trader-less: price is `repairCostEur`. Each repair shaves the ceiling by
   * `REPAIR_MAX_LOSS_FRAC` (`ItemInstance.maxDurability`). Answered via
   * `invResult` then `profile`, same `opId` counter as `invOp`.
   */
  | { t: "repair"; opId: number; iid: string }
  /**
   * Hand `iids` over against ONE `bringItems` quest — the only way its progress
   * moves (`applyQuestTurnIn`). `iids` are TOP-LEVEL instances in the stash or
   * kit grids (not nested, equipped or a fitted magazine); invisible ones are
   * skipped and the op fails if nothing is left. The server consumes up to the
   * remainder in list order, splitting the last stack. Answered via `invResult`
   * then `profile`.
   */
  | { t: "questTurnIn"; opId: number; questId: string; iids: string[] }
  /*
   * The hideout verbs. All menu only, all answered like `buy`/`sell` (an
   * `invResult` with the same `opId`, then a `profile`). None carries a
   * timestamp: the server advances the hideout to its own clock first.
   */
  /**
   * Start a module's next level. Costs are taken immediately, no cancel/refund.
   * Refused per `buildProblem`.
   */
  | { t: "hideoutBuild"; opId: number; moduleId: string }
  /**
   * Start a recipe. Inputs leave now; output arrives via `hideoutCollect`.
   * Refused per `craftProblem`.
   */
  | { t: "hideoutCraft"; opId: number; craftId: string }
  /**
   * Take a finished craft's output into the stash. Separate so a full stash can
   * fail and the craft stays done.
   */
  | { t: "hideoutCollect"; opId: number; craftId: string }
  /** Empty a production module's buffer into the stash. Same story as
   *  `hideoutCollect`: it can fail on space, and nothing is lost when it does. */
  | { t: "hideoutHarvest"; opId: number; moduleId: string }
  /**
   * Feed ONE canister (an instance in the stash) into the tank. Refused when a
   * full canister would not fit.
   */
  | { t: "hideoutFuel"; opId: number; iid: string }
  /**
   * The generator switch. `on: false` stops every powered craft; `on: true` is
   * refused with no generator or an empty tank.
   */
  | { t: "hideoutPower"; opId: number; on: boolean }
  /**
   * One finished gym session. `skill` ∈ `TRAINABLE_SKILLS`, `score` ∈ [0, 1]
   * from the minigame. The score only buys the top half of one session
   * (`trainingGain`), and the cooldown — the real budget — is stamped by the
   * server, so a lying client gains at most a factor of two on one session.
   * Answered via `invResult` then `profile`; refused per `trainProblem`.
   */
  | { t: "hideoutTrain"; opId: number; skill: string; score: number }
  /*
   * Friends and party. All menu only, all answered with `invResult` (same
   * `opId` counter — "verdict on op N", not "inventory result") followed by
   * `friends`/`partyState`. Everyone is named by USERNAME: permanent, unique,
   * case-insensitive; display names are neither and connection ids do not
   * exist for offline friends.
   */
  /**
   * Ask `username` to be friends. Idempotent; sending to someone whose request
   * you already have accepts it (the only button a player without an incoming
   * list has). `friendRespond` is the explicit path.
   */
  | { t: "friendRequest"; opId: number; username: string }
  /** Answer an incoming request. Declining drops it silently. */
  | { t: "friendRespond"; opId: number; username: string; accept: boolean }
  /**
   * Unfriend, or withdraw a request you sent — one gesture, "remove this row".
   * Mutual.
   */
  | { t: "friendRemove"; opId: number; username: string }
  /**
   * Invite a friend into your party, creating one if needed. Target must be a
   * friend, online and in the menu. Refused when full (`PARTY_MAX`), when the
   * invitee is in a party, or when you are not the leader.
   */
  | { t: "partyInvite"; opId: number; username: string }
  /**
   * Answer the current pending invite (at most one; a newer invite replaces an
   * older).
   */
  | { t: "partyRespond"; opId: number; accept: boolean }
  /**
   * Leave the party. Leadership passes to the next member in join order; the
   * party dies with its last member.
   */
  | { t: "partyLeave"; opId: number }
  /** Throw a member out. LEADER ONLY, and the leader cannot kick themselves —
   *  that is `partyLeave`, which has the succession rule attached. */
  | { t: "partyKick"; opId: number; username: string }
  /**
   * One inventory edit. In a raid: the PMC's inventory plus the open
   * container. In the menu: the kit is the `Inventory` and the stash is the
   * `"container"`. `opId` is strictly increasing per connection and echoed in
   * `invResult`; the op is applied optimistically and `ok: false` means take
   * the `inv`/`containerState` sent alongside. The server adds the checks
   * `applyInventoryOp` cannot: liveness, ownership, `LOOT_REACH_M`, rate.
   */
  | { t: "invOp"; opId: number; op: InventoryOp }
  /**
   * Start looking inside a world container (map spawn or corpse). Replied with
   * `containerState`; later changes are pushed. Only one open at a time.
   */
  | { t: "open"; containerId: string }
  /** Stop looking. Sent on closing the panel, walking away, or dying. */
  | { t: "close"; containerId: string }
  /**
   * Take a dropped item. No target cell: the server auto-places in canonical
   * scan order, matching the client's prediction.
   */
  | { t: "pickup"; itemId: string }
  /**
   * Begin using a med — or eating/drinking, if `iid` names a loot item with a
   * `consume` block. The server picks the limb (`resolveMedTarget`), runs
   * `useTimeSec` (`YouSnap.medEndTick`) and applies at the end. Nothing to
   * treat or gain → rejected without cost.
   */
  | { t: "useMed"; iid: string }
  /**
   * Pull the pin. The fuse starts NOW; a grenade held too long goes off in the
   * hand. Silently refused when invalid or another is armed.
   */
  | { t: "grenadeArm"; iid: string }
  /**
   * Look into the magazine of the gun in hand. Hands busy for `MAG_CHECK_SEC`
   * (`YouSnap.weapon.checking`), then the box is `magKnown`. Silently refused
   * when dead, empty-handed, no magazine, reloading or already checking.
   */
  | { t: "magCheck" }
  /**
   * Let go. Same geometry as `fire`; the server spawns at `dir *
   * THROW_SPEED_MPS` and broadcasts `grenadeThrown`.
   */
  | { t: "grenadeThrow"; tick: number; origin: Vec3; dir: Vec3 }
  /**
   * Work a window: `"smash"` an intact pane within `GLASS_REACH_M`, `"climb"`
   * through a broken one (the server puts you down on the far side). `id`
   * indexes `MapDef.glass`. Silently refused otherwise.
   */
  | { t: "glass"; id: number; act: GlassAct };

/** What a player is doing to a window pane. */
export type GlassAct = "smash" | "climb";

export type ClientMsgType = ClientMsg["t"];

// ---------------------------------------------------------------------------
// Server -> client
// ---------------------------------------------------------------------------

/** How another entity looks to you this snapshot. */
export interface EntitySnap {
  id: string;
  kind: EntityKind;
  name?: string;
  /**
   * What kind of scav this is. Sticky per recipient like `name` (sent once).
   * Absent = `"wilderer"` or a human. Public because it is visible.
   */
  role?: ScavRole;
  /**
   * Which animal this is — on every snapshot of an `"animal"`. Not memo'd per
   * recipient: animals have no `snapMemo`, and an animal leaving and
   * re-entering the interest radius is a fresh entity to the client.
   */
  species?: WildlifeSpecies;
  pos: Vec3;
  yaw: number;
  pitch: number;
  stance: Stance;
  anim: AnimState;
  /**
   * What this entity is visibly wearing — the only public inventory data.
   * `weapon` is a def id (`null` empty-handed); the rest are booleans so the
   * vest's quality does not leak. Absent → default silhouette.
   */
  gear?: { weapon: string | null; vest: boolean; helmet: boolean; backpack: boolean };
  /**
   * True while changing a magazine — public because it is visible. Omitted
   * rather than `false` (wire cost).
   */
  reload?: boolean;
  /**
   * Peeking: −1 left, +1 right. Public because the hitboxes move with it.
   * Omitted at 0.
   */
  lean?: -1 | 1;
  /**
   * The mounted device switched ON, if any — only the kind, never the part.
   * Omitted when dark (absent = not lit) and never sent for the dead.
   */
  tac?: TacticalKind;
  /**
   * In the recipient's party. Per-recipient; omitted when false. Only colours
   * the tag — friendly fire stays on.
   */
  team?: true;
}

/** Authoritative state of the receiving player — used for reconciliation. */
export interface YouSnap {
  pos: Vec3;
  vel: Vec3;
  onGround: boolean;
  stance: Stance;
  stamina: number;
  /**
   * Limb hp in `LIMB_IDS` order. Raw floats — the client's predicted health
   * must match exactly.
   */
  hp: number[];
  /** Bit `i` set = `LIMB_IDS[i]` is bleeding. See `limbMaskFrom`. */
  bleedMask: number;
  /** Bit `i` set = `LIMB_IDS[i]` is fractured. */
  fracMask: number;
  dead: boolean;
  /**
   * Your own weapon only. `mag` is always the TRUE count (prediction
   * reconciles against it); `unknown` is present when the player has not
   * counted it (`loadedRoundsKnown`) and the HUD shows "?"; `checking` while a
   * `magCheck` is in the hands. Both omitted rather than `false`.
   */
  weapon: { mag: number; reloading: boolean; unknown?: true; checking?: true };
  /**
   * Tick a med use completes on (a tick, so a late snapshot still ends the bar
   * at the right moment). Absent when idle.
   */
  medEndTick?: number;
  /**
   * Hunger and thirst, 0..`NEEDS_MAX`, a tenth's precision. On every snapshot
   * (they move every tick). Absent = both full. The client folds
   * `moveModsFromNeeds` of this into its prediction.
   */
  needs?: { energy: number; hydration: number };
  /**
   * The extraction you stand in and seconds left. Absent when in none.
   * `secLeft` is recomputed each snapshot.
   */
  extract?: { name: string; secLeft: number };
}

/**
 * One mark on the ground: a footprint or a drop of blood (see `sim/tracks.ts`).
 * `id` is the raid-unique dedupe key; `pos` is the FEET position (no floor
 * search needed); `yaw` is the direction of travel; `age` is seconds old at
 * snapshot time so a late client fades it from the right point.
 */
export interface TrackSnap {
  id: number;
  k: TrackKind;
  pos: Vec3;
  yaw: number;
  age: number;
}

export interface Snapshot {
  tick: number;
  /** Highest `InputCmd.seq` the server has consumed from this client. */
  lastAckSeq: number;
  you: YouSnap;
  entities: EntitySnap[];
  /**
   * Marks this client has not been told about yet, within `TRACK_SEND_RANGE_M`.
   * Each sent once; omitted when none.
   */
  tracks?: TrackSnap[];
}

/** Why a player died — what the death screen puts under the headline. */
export type DeathCause = "shot" | "blast" | "bleed" | "starve";

export type GameEvent =
  | { t: "playerJoined"; id: string; name: string }
  | { t: "playerLeft"; id: string }
  /**
   * Someone fired — tracers, flash, audio. The shooter is excluded (they
   * already played it). `weaponId` is the gun's def id, so bystanders pick the
   * right velocity and report; optional for old captures.
   */
  | { t: "shot"; id: string; origin: Vec3; dir: Vec3; weaponId?: string }
  | {
      t: "hit";
      shooterId: string;
      targetId: string;
      limb: LimbId;
      damage: number;
    }
  /**
   * Somebody died. The forensics ride along so the victim's screen needs no
   * second lookup: `killerName`, `killerEye` (the killer's eye at that tick)
   * and `distM` only when the killer is somebody else; `weaponId` (gun or
   * grenade def) and `limb` (the killing hit) for shot/blast.
   */
  | {
      t: "death";
      id: string;
      killerId: string;
      cause: DeathCause;
      killerName?: string;
      killerEye?: Vec3;
      distM?: number;
      weaponId?: string;
      limb?: LimbId;
    }
  /** An item hit the ground — everything needed to spawn a mesh. */
  | { t: "itemDropped"; id: string; defId: string; pos: Vec3 }
  /** ...and it is gone again. Despawn the mesh; no reason is given, because
   *  who took it is not something bystanders get to know. */
  | { t: "itemTaken"; id: string }
  /**
   * A grenade left a hand. The client flies the arc itself with `stepGrenade`
   * (no per-tick snapshots); `explodeTick` fixes the fuse. Thrower INCLUDED.
   */
  | {
      t: "grenadeThrown";
      id: string;
      ownerId: string;
      defId: string;
      pos: Vec3;
      vel: Vec3;
      explodeTick: number;
    }
  /** ...and it went off. `pos` is authoritative; the client's own arc was a guess. */
  | { t: "grenadeExploded"; id: string; defId: string; pos: Vec3 }
  /**
   * Birds went up at `pos` — something startled an animal. Position only: a
   * smudge on the map saying "somebody moved through there", fed to the AI as
   * the same noise.
   */
  | { t: "birdFlush"; pos: Vec3 }
  /**
   * A scav shouted. `id` is his entity id, `v` the variant of `kind` picked by
   * the server — see `ai/barks.ts`. Delivered only to the near.
   */
  | { t: "bark"; id: string; kind: BarkKind; v: number }
  /**
   * A pane broke — shards, the sound, and drop its collision. `id` indexes
   * `MapDef.glass`.
   */
  | { t: "glass"; id: number };

/**
 * Where the client gets the raid's map: `static` = compiled into shared
 * (`getMapById`; Testfield only); `download` = fetch `url`, `deserializeMapDef`,
 * check `mapHash` against `hash`. `url` is RELATIVE — served by the same HTTP
 * server as the WebSocket, so the client resolves it against its own server URL.
 */
export type MapSource =
  | { kind: "static" }
  /**
   * `bytes` is the UNCOMPRESSED size (what `fetch` lets the client count).
   * Advisory; absent → indeterminate progress.
   */
  | { kind: "download"; url: string; hash: string; bytes?: number };

/** How a raid ended for one player. Wire vocabulary; `economy/quests.ts`
 *  settles against exactly these three cases. */
export type RaidResult = "extracted" | "died" | "mia";

/** The meta state of one account, whole. Not a delta: it IS the resync path. */
export interface ProfileWire {
  name: string;
  euros: number;
  /** The stash: one big grid (`STASH_W` x `STASH_H`). */
  stash: GridState;
  /** The kit the PMC takes into the next raid — same shape as a raid inventory. */
  kit: Inventory;
  quests: QuestState[];
  /**
   * The cellar, already advanced to the server clock and stamped in
   * `lastAdvanceMs`, so the client can run `advanceHideout` from there.
   */
  hideout: HideoutState;
  /**
   * Experience, skills and lifetime tally. Required: every level gate in the
   * menu reads it, so the server always sends something (`sanitizeProgress`).
   */
  progress: ProgressState;
  /**
   * Turnover per trader — the only stored half of standing (the rest derives
   * from `quests`). Required, like `progress`: the shelf is drawn from it.
   */
  loyalty: LoyaltyState;
}

/**
 * Where an account is, as a friend sees it — the only thing beyond "online"
 * they learn.
 */
export type SocialPhase = "menu" | "queued" | "raid";

/** One person on your friends list. */
export interface FriendEntry {
  /** The account handle — unique, case-insensitive, what you add them by. */
  username: string;
  /** Their display name. Neither unique nor stable; for the eyes only. */
  name: string;
  online: boolean;
  /** Present only while `online`; "offline and in a raid" is not a state. */
  phase?: SocialPhase;
}

/**
 * One end of a pending friend request: username and display name, no presence.
 */
export interface FriendRef {
  username: string;
  name: string;
}

/** One member of a party, as everyone else in it sees them. */
export interface PartyMemberWire {
  username: string;
  name: string;
  online: boolean;
  /**
   * Required here: the party panel's job is to show readiness. A dropped member
   * keeps their last phase with `online: false`.
   */
  phase: SocialPhase;
  /** Exactly one member carries `true`: the one who queues the party. */
  leader: boolean;
}

/** What the raid-end screen is made of. */
export interface RaidSummary {
  result: RaidResult;
  /** Display name of the exit used, when `result` is "extracted". */
  extractName?: string;
  /** Display name of whoever killed you, when `result` is "died". */
  killedBy?: string;
  scavKills: number;
  pmcKills: number;
  /** Bosses killed. Its own number rather than part of `scavKills`, because
   *  killing the Hunter is the line of the summary a player reads first. */
  bossKills: number;
  /** Quest ids whose progress moved (or completed) at this settlement. */
  questsAdvanced: string[];
  /** Euro rewards from quests NEWLY completed at this settlement. */
  rewardEuros: number;
  /**
   * Experience from this raid, whole (kills, extraction, loot, quests) — one
   * number, not a breakdown.
   */
  xpGained: number;
  /** Level going in and coming out; the screen celebrates when they differ. */
  levelBefore: number;
  levelAfter: number;
}

export type ServerMsg =
  | {
      t: "welcome";
      playerId: string;
      tick: number;
      mapId: string;
      mapSource: MapSource;
      seed: number;
      spawn: Vec3;
      /**
       * Tick this raid hard-ends on (everyone inside goes MIA). Absent on a
       * direct-mode server.
       */
      raidEndTick?: number;
      /** Time of day and weather. Absent = `DEFAULT_ENV`. */
      env?: RaidEnv;
      /**
       * Your skill multipliers for this raid, sent once — skills do not move
       * during a raid. Both sides feed the same numbers into movement, so
       * prediction stays honest. Absent = `DEFAULT_SKILL_MODS`.
       */
      skillMods?: SkillMods;
    }
  | { t: "snapshot"; snap: Snapshot }
  /** You were flashed. Per-recipient; `sec` is the white-out length. */
  | { t: "flash"; sec: number }
  | { t: "event"; ev: GameEvent }
  | { t: "pong"; cNow: number; sNow: number }
  | { t: "error"; code: string; msg: string }
  /**
   * The authoritative inventory, whole — not a delta; it IS the resync path.
   * Sent after `welcome`, after any server-side change, and after any failed
   * `invOp`.
   */
  | { t: "inv"; inv: Inventory }
  /**
   * Contents of an open world container. Sent on `open` and re-sent to other
   * viewers when an op changes it. `kind` may be `"corpse"`.
   */
  | {
      t: "containerState";
      id: string;
      kind: ContainerKind | "corpse";
      name: string;
      grid: GridState;
      /**
       * Worn slots of a body — present for `"corpse"` only; `grid` is then the
       * pockets. See `inventory/body.ts`.
       */
      equipment?: EquipmentRecord;
    }
  /**
   * The verdict on one op. `reason` is an `InvOpReason` code or one of the
   * server's own ("rate", "reach", "dead", and for shelf gates "level",
   * "tier", "quest" — checked in that order by `offerLock`, the same code the
   * client greys the row with). On `ok: false` an `inv` (and `containerState`)
   * is sent alongside.
   */
  | { t: "invResult"; opId: number; ok: boolean; reason?: string }
  /**
   * Every item on the ground, sent once after `welcome`;
   * `itemDropped`/`itemTaken` from there on.
   */
  | { t: "worldItems"; items: { id: string; defId: string; pos: Vec3 }[] }
  /**
   * Panes already broken when you joined, as `MapDef.glass` indices; the
   * `glass` event from there on.
   */
  | { t: "glassState"; broken: number[] }
  /**
   * The authoritative meta state, whole. Sent after `hello` and after every
   * change.
   */
  | { t: "profile"; profile: ProfileWire }
  /**
   * Where the queue stands. `startsInMs` is `null` when waiting on player
   * count.
   */
  | { t: "queueStatus"; queued: number; startsInMs: number | null }
  /**
   * Your raid is over. A `profile` follows with the consequences applied; the
   * summary is a readout only.
   */
  | { t: "raidEnd"; summary: RaidSummary }
  /**
   * The friends state, whole. Re-sent after every change including presence
   * — one code path for "friends changed". `incoming` are requests to you,
   * `outgoing` yours; neither carries presence.
   */
  | { t: "friends"; friends: FriendEntry[]; incoming: FriendRef[]; outgoing: FriendRef[] }
  /**
   * The party you are in, or `null` (an empty party cannot exist). `members`
   * in join order, which is also succession order. Pushed on every change.
   */
  | { t: "partyState"; party: { members: PartyMemberWire[] } | null }
  /**
   * An invite prompt, answered with `partyRespond`. At most one outstanding;
   * this replaces it. A stale invite is refused when answered.
   */
  | { t: "partyInvited"; from: FriendRef };

export type ServerMsgType = ServerMsg["t"];

// ---------------------------------------------------------------------------
// Button helpers
// ---------------------------------------------------------------------------

export function hasButton(buttons: number, bit: number): boolean {
  return (buttons & bit) !== 0;
}

/**
 * Lean direction a button mask asks for. Shared so camera, server hitboxes
 * and tests agree by construction. Both keys held is 0 — a pure fold, so a
 * replayed command always produces the same lean.
 */
export function leanFromButtons(buttons: number): -1 | 0 | 1 {
  const left = (buttons & BTN.LEAN_L) !== 0;
  const right = (buttons & BTN.LEAN_R) !== 0;
  if (left === right) return 0;
  return left ? -1 : 1;
}
